import React from 'react';

import Loadable from 'react-loadable';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import { fg } from '@atlaskit/platform-feature-flags';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import { DEFAULT_GRAY_COLOR } from '@atlassian/color-picker';
import {
	CornerStyle,
	type CustomSitesExtensionProviderProps,
	getExtensionManifest,
	type HubDefaultParameters,
	RenderVariant,
	Height as Size,
} from '@atlassian/company-hub-common';

import {
	CARDS_EXTENSION_KEY,
	CARDS_EXTENSION_TYPE,
	CARDS_EXTENSION_VERSION_3,
	CARDS_EXTENSION_VERSION_4,
	CARDS_EXTENSION_VERSION_5,
	CARDS_IMAGES_V5,
} from '../../constants';
import {
	Alignment,
	type CardsExtensionParameters,
	CardsLayout,
	ImagePosition,
	Orientation,
} from '../../types';
import { type CardsExtensionProps } from '../cards-extension';

import { CARDS_IMAGES_V3 } from './constants';
import { messages } from './messages';

const CardsExtension = Loadable({
	loader: async () =>
		(await import(/* webpackChunkName: "@atlaskit-internal_CardsExtension" */ '../cards-extension'))
			.CardsExtension,
	loading: () => <UFOLoadHold name="cards-extension-loader"></UFOLoadHold>,
});

export const createCard = (imageSrc: string, imageAltText: string) => ({
	// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
	cardId: uuid(),
	link: '',
	isLinkUserEdited: false,
	title: '',
	isTitleUserEdited: false,
	description: '',
	isDescriptionUserEdited: false,
	imageSrc,
	imagePosition: ImagePosition.MIDDLE,
	imageAltText,
});

let cards: ReturnType<typeof createCard>[] | null = null;
export const getDefaultCards = () => {
	if (cards) {
		return cards;
	}

	const initialCards = fg('company_hub_cards_visual_update') ? CARDS_IMAGES_V5 : CARDS_IMAGES_V3;

	return (cards = initialCards.map(({ src, alt }) => createCard(src, alt)));
};

type GetCardsExtensionParametersProps = {
	defaultParameters?: HubDefaultParameters;
	extensionTitle: string;
	isCardsV1GateEnabled: boolean;
	isCardsV2GateEnabled: boolean;
	isDynamicCardsAIDescriptionsEnabled: boolean;
};

export const getCardsExtensionParameters = ({
	extensionTitle,
	isCardsV1GateEnabled,
	isCardsV2GateEnabled,
	isDynamicCardsAIDescriptionsEnabled,
	defaultParameters,
}: GetCardsExtensionParametersProps): CardsExtensionParameters => {
	const parameters: CardsExtensionParameters = {
		size: Size.MEDIUM,
		cards: getDefaultCards(),
		isAvatarShown: true,
		isPublishDateShown: false,
		extensionTitle,
		version: CARDS_EXTENSION_VERSION_3,
		renderVariant: RenderVariant.MANUAL,
		isSegmentationShown: false,
		orientation: isCardsV1GateEnabled ? Orientation.STACKED : Orientation.ROW,
		alignment: isCardsV1GateEnabled ? Alignment.CENTER : Alignment.LEFT,
	};

	parameters.isAutoscrollEnabled ??= false;

	if (isCardsV1GateEnabled) {
		parameters.isAvatarShown = false;
		parameters.backgroundColor ??=
			defaultParameters?.backgroundColor ?? token('elevation.surface.overlay');
		parameters.textColor ??= defaultParameters?.textColor ?? token('color.text');
		parameters.cornerStyle ??= CornerStyle.ROUNDED;
		parameters.isSocialStatsShown ??= false;
		parameters.isIconShown ??= true;
		parameters.version = CARDS_EXTENSION_VERSION_4;
	}

	if (isCardsV2GateEnabled) {
		parameters.layout ??= CardsLayout.FIXED_IMAGE_HEIGHT;
		parameters.isPaddingEnabled ??= true;
		parameters.isLabelShown ??= true;
		parameters.borderColor ??= DEFAULT_GRAY_COLOR;
		parameters.version = CARDS_EXTENSION_VERSION_5;
	}

	if (isDynamicCardsAIDescriptionsEnabled) {
		parameters.isDescriptionShown = true;
	}

	return parameters;
};

export const cardsExtensionProvider = ({
	contentId,
	propsProvider,
	mode,
	defaultParameters,
}: CardsExtensionProps) => {
	return async ({
		editorActions,
		openConfigPanel,
		intl,
		setUpsellModalSource,
	}: CustomSitesExtensionProviderProps) => {
		const parameters = getCardsExtensionParameters({
			extensionTitle: intl.formatMessage(messages.linkCardsMacroTitle),
			isCardsV1GateEnabled: fg('company_hub_cards_visual_update'),
			isCardsV2GateEnabled: fg('company_hub_cards_v2'),
			isDynamicCardsAIDescriptionsEnabled: fg('company_hub_dynamic_cards_ai_descriptions'),
			defaultParameters,
		});
		return new DefaultExtensionProvider([
			getExtensionManifest<CardsExtensionParameters>({
				title: intl.formatMessage(messages.linkCardsMacroTitle),
				type: CARDS_EXTENSION_TYPE,
				key: CARDS_EXTENSION_KEY,
				description: intl.formatMessage(messages.linkCardsMacroDescription),
				icons: {
					'48': () =>
						import(
							/* webpackChunkName: "@atlaskit-internal_loadable-CardsExtensionIcon" */
							'../assets/cards-extension-icon'
						).then((mod) => mod.CardsExtensionIcon),
				},
				parameters,
				update: async () => {
					const selectedNode = editorActions?.getSelectedNode()?.toJSON();
					const selectedLocalId = selectedNode?.attrs?.localId;

					if (!!selectedLocalId && openConfigPanel) {
						openConfigPanel(selectedLocalId);
					}
				},
				render:
					async () =>
					({ node }: any) => {
						return (
							<CardsExtension
								extensionNode={node}
								contentId={contentId}
								propsProvider={propsProvider}
								mode={mode}
							/>
						);
					},
				quickInsertAction: async () =>
					Promise.resolve({
						type: 'extension',
						key: CARDS_EXTENSION_KEY,
						attrs: {
							extensionType: CARDS_EXTENSION_TYPE,
							extensionKey: CARDS_EXTENSION_KEY,
							layout: 'default',
							parameters,
						},
					}),
				source: 'premiumCardsInsert',
				setUpsellModalSource,
			}),
		]);
	};
};
